import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

type Identity = {
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  company?: string | null;
};

let identity: Identity = {};

function parseUA(ua: string) {
  const browser = /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari" : "Unknown";
  const os = /Windows/.test(ua) ? "Windows"
    : /Mac OS X/.test(ua) ? "macOS"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad|iPod/.test(ua) ? "iOS"
    : /Linux/.test(ua) ? "Linux" : "Unknown";
  const deviceType = /Mobi|Android|iPhone|iPod/.test(ua) ? "Mobile"
    : /iPad|Tablet/.test(ua) ? "Tablet" : "Desktop";
  return { browser, os, deviceType };
}

function deviceContext() {
  if (typeof window === "undefined") return {};
  const ua = navigator.userAgent || "";
  const { browser, os, deviceType } = parseUA(ua);
  return {
    browser,
    operating_system: os,
    device_type: deviceType,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || null,
  };
}

export function setIdentity(next: Identity) {
  identity = { ...identity, ...next };
  try {
    if (next.userId && window.clarity) {
      window.clarity("identify", next.userId, undefined, undefined, next.company || undefined);
      if (next.company) window.clarity("set", "company", next.company);
    }
    if (next.userId && window.gtag) {
      window.gtag("set", { user_id: next.userId });
    }
  } catch {
    /* silent */
  }
}

export function clearIdentity() {
  identity = {};
}

export function getIdentity() {
  return identity;
}

type EventProps = {
  category?: string;
  page_path?: string;
  page_title?: string;
  page_slug?: string;
  section_name?: string;
  feature_name?: string;
  case_study_name?: string;
  case_study_slug?: string;
  button_label?: string;
  target_url?: string;
  metadata?: Record<string, unknown>;
};

export async function trackEvent(eventName: string, props: EventProps = {}) {
  const ctx = deviceContext();
  const path = props.page_path ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const title = props.page_title ?? (typeof document !== "undefined" ? document.title : "");

  // GA4
  try {
    if (window.gtag) {
      window.gtag("event", eventName, {
        event_category: props.category,
        page_path: path,
        page_title: title,
        page_slug: props.page_slug,
        section_name: props.section_name,
        feature_name: props.feature_name,
        case_study_slug: props.case_study_slug,
        button_label: props.button_label,
        target_url: props.target_url,
      });
    }
  } catch { /* silent */ }

  // Clarity custom event + tags
  try {
    if (window.clarity) {
      window.clarity("event", eventName);
      if (props.section_name) window.clarity("set", "section", props.section_name);
      if (props.case_study_slug) window.clarity("set", "case_study", props.case_study_slug);
    }
  } catch { /* silent */ }

  // Internal DB (only when logged in — RLS requires auth.uid())
  if (!identity.userId) return;

  try {
    const row = {
      user_id: identity.userId,
      user_email: identity.email ?? null,
      user_name: identity.name ?? null,
      user_company: identity.company ?? null,
      event_name: eventName,
      event_category: props.category ?? null,
      page_path: path,
      page_title: title,
      page_slug: props.page_slug ?? null,
      section_name: props.section_name ?? null,
      feature_name: props.feature_name ?? null,
      case_study_name: props.case_study_name ?? null,
      case_study_slug: props.case_study_slug ?? null,
      button_label: props.button_label ?? null,
      target_url: props.target_url ?? null,
      referrer: ctx.referrer ?? null,
      device_type: ctx.device_type ?? null,
      browser: ctx.browser ?? null,
      operating_system: ctx.operating_system ?? null,
      screen_resolution: ctx.screen_resolution ?? null,
      language: ctx.language ?? null,
      timezone: ctx.timezone ?? null,
      clarity_user_id: identity.userId ?? null,
      metadata: (props.metadata ?? null) as unknown,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await supabase.from("user_activity_events").insert(row as any);
  } catch (err) {
    // Never break UX on tracking failure
    console.warn("[analytics] insert failed", err);
  }
}

/** Install a global click delegate that auto-tracks `[data-track-event]` clicks. */
export function installClickDelegate() {
  if (typeof document === "undefined") return;
  if ((window as unknown as { __fpClickInstalled?: boolean }).__fpClickInstalled) return;
  (window as unknown as { __fpClickInstalled?: boolean }).__fpClickInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest<HTMLElement>("[data-track-event]");
      if (!el) return;
      const eventName = el.dataset.trackEvent!;
      const label =
        el.dataset.trackLabel ||
        (el as HTMLAnchorElement).innerText?.trim().slice(0, 80) ||
        undefined;
      const href = (el as HTMLAnchorElement).href;
      trackEvent(eventName, {
        category: el.dataset.trackCategory,
        section_name: el.dataset.trackSection,
        feature_name: el.dataset.trackFeature,
        case_study_slug: el.dataset.trackCaseStudy,
        button_label: label,
        target_url: el.dataset.trackHref || href || undefined,
      });
    },
    { capture: true },
  );
}
